<template>
  <div class="step__block-dimensions">
    <DimensionsTable v-model="activeOverdoor!.dimensions" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from "vue";
import { DimensionsTable } from "@/shared";
import { useOverdoorStore } from "@/stores/new/overdoor.store";
import { useDoorsStore } from "@/stores/new/doors.store";
import { usePartitionStore } from "@/stores/new/partition.store";
import { Partition, Door } from "@/app/domain";

const overdoorStore = useOverdoorStore();
const doorsStore = useDoorsStore();
const partitionsStore = usePartitionStore();

const activePartition = computed(() => partitionsStore.activePartitions);
const activeDoor = computed(() => doorsStore.activeDoor);
const activeOverdoor = computed(() => overdoorStore.activeOverdoor);

const setupOverdoorDimensions = () => {
  if (!activeOverdoor.value) return;

  if (activeOverdoor.value.isNoneOverdoor()) {
    return activeOverdoor.value.resetDimensions();
  }

  if (activeOverdoor.value.isFiller()) {
    return activeOverdoor.value.resetDimensions();
  }

  if (activeOverdoor.value.isTransom()) {
    return recalculateTransomDimensions();
  }
};

const recalculateTransomDimensions = () => {
  if (!activeOverdoor.value?.isTransom()) return;
  if (!activePartition.value || !activeDoor.value) return;

  const remainingHeight = activeOverdoor.value.checkTransomHeight(
    activePartition.value as unknown as Partition,
    activeDoor.value as unknown as Door,
  );

  if (remainingHeight.getLength().toSquareFt().getValue() === 0) {
    activeOverdoor.value.structureType = "none";
  }

  activeOverdoor.value.setTransomDimensions(
    activePartition.value as unknown as Partition,
    activeDoor.value as unknown as Door,
  );
};

watch(
  () => activeOverdoor.value?.structureType,
  () => {
    setupOverdoorDimensions();
  },
);

watch(
  () => [
    activePartition.value?.dimensions.width,
    activePartition.value?.dimensions.height,
    activeDoor.value?.dimensions.width,
    activeDoor.value?.dimensions.height,
  ],
  () => {
    recalculateTransomDimensions();
  },
  { deep: true },
);

onMounted(() => {
  setupOverdoorDimensions();
});
</script>

<style lang="scss" scoped></style>
